import { useCallback, useEffect, useRef } from 'preact/hooks';
import { batch } from '@preact/signals';
import { useCompany } from './useCompany';
import { useToast } from './useToast';
import { startConversation, sendMessage, applyToField as applyToFieldAPI, completeConversation, updateFeedback, updateMessageFeedback, } from '@/api/aiChat';
import { ChatEntityTypes, ChatLocations, ChatViewModes } from '@/constants/aiChat';
import { aiChatState, fieldUpdateHandler, initialSessionContext, lastClosedSession, } from '@/state/aiChat';
// --- Helpers ---
const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
const getTurnLimitFields = (session) => ({
    approachingTurnLimit: session.ApproachingTurnLimit || false,
    remainingTurns: session.RemainingTurns ?? 0,
});
/** Strip markdown bold/italic and surrounding quotes */
function cleanMarkdown(value) {
    return value
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/^["']|["']$/g, '')
        .trim();
}
/** Build a ChatMessage from the latest assistant message in a session response,
 *  tracking its raw index in the backend Messages array for feedback. */
function extractLatestAssistantMessage(session) {
    // Find the last assistant message by scanning the full array in reverse
    // so we capture the raw backend index, not a filtered index.
    let lastIdx = -1;
    for (let i = session.Messages.length - 1; i >= 0; i--) {
        if (session.Messages[i].Role === 'assistant') {
            lastIdx = i;
            break;
        }
    }
    if (lastIdx === -1)
        return null;
    const latest = session.Messages[lastIdx];
    return {
        id: generateMessageId(),
        role: 'assistant',
        content: latest.Content,
        timestamp: new Date(latest.Timestamp),
        generatedVariations: latest.GeneratedVariations,
        targetFieldName: latest.TargetFieldName,
        backendIndex: lastIdx,
    };
}
/** Apply an assistant response from the API to chat state.
 *  Also backfills backend indices for any local messages that don't have one yet
 *  (e.g. the user message we just sent). */
function applySessionResponse(session) {
    const assistantMessage = extractLatestAssistantMessage(session);
    const turnLimits = getTurnLimitFields(session);
    // Backfill backendIndex for the most recent user message if missing.
    // The last 'user' message in the backend array is the one we just sent.
    let messages = aiChatState.value.messages;
    const lastLocal = messages[messages.length - 1];
    if (lastLocal && lastLocal.role === 'user' && lastLocal.backendIndex == null) {
        let lastUserIdx = -1;
        for (let i = session.Messages.length - 1; i >= 0; i--) {
            if (session.Messages[i].Role === 'user') {
                lastUserIdx = i;
                break;
            }
        }
        if (lastUserIdx >= 0) {
            messages = messages.map((m, i) => i === messages.length - 1 ? { ...m, backendIndex: lastUserIdx } : m);
        }
    }
    if (assistantMessage) {
        aiChatState.value = {
            ...aiChatState.value,
            messages: [...messages, assistantMessage],
            isLoading: false,
            suggestClose: session.SuggestClose || false,
            ...turnLimits,
        };
    }
    else {
        aiChatState.value = { ...aiChatState.value, messages, isLoading: false, ...turnLimits };
    }
}
// --- Feedback sub-hook ---
function useAIChatFeedback(companyId, currentWorkspace) {
    const provideFeedback = useCallback(async (rating) => {
        const sessionId = aiChatState.value.sessionId || lastClosedSession.value;
        if (!sessionId || !companyId || !currentWorkspace) {
            console.warn('No active or recent session for feedback');
            return;
        }
        try {
            batch(() => {
                aiChatState.value = { ...aiChatState.value, rating, showDelayedFeedback: false };
                lastClosedSession.value = null;
            });
            await updateFeedback(companyId, currentWorkspace.Id, sessionId, rating);
        }
        catch (error) {
            console.error('Error providing feedback:', error);
        }
    }, [companyId, currentWorkspace]);
    const provideMessageFeedback = useCallback(async (messageId, rating, comment) => {
        const state = aiChatState.value;
        if (!state.sessionId || !companyId || !currentWorkspace)
            return;
        const message = state.messages.find(m => m.id === messageId);
        if (!message || message.backendIndex == null)
            return;
        aiChatState.value = {
            ...state,
            messages: state.messages.map(m => m.id === messageId ? { ...m, rating, feedbackComment: comment } : m),
        };
        try {
            await updateMessageFeedback(companyId, currentWorkspace.Id, state.sessionId, message.backendIndex, rating, comment);
        }
        catch (error) {
            console.error('Error providing message feedback:', error);
        }
    }, [companyId, currentWorkspace]);
    const hideDelayedFeedback = useCallback(() => {
        aiChatState.value = { ...aiChatState.value, showDelayedFeedback: false };
    }, []);
    return { provideFeedback, provideMessageFeedback, hideDelayedFeedback };
}
// --- Main hook ---
/**
 * Hook for reading AI chat state and calling actions
 * Components that use this will re-render when state changes
 */
export function useAIChat() {
    // Reading .value during render subscribes the calling component to changes
    const state = aiChatState.value;
    const { companyId, currentWorkspace } = useCompany();
    const { showError, showSuccess } = useToast();
    // --- Domain isolation: hard-reset chat when company or workspace changes ---
    const prevCompanyRef = useRef(companyId);
    const prevWorkspaceRef = useRef(currentWorkspace?.Id);
    useEffect(() => {
        const prevCompany = prevCompanyRef.current;
        const prevWorkspace = prevWorkspaceRef.current;
        const currWorkspaceId = currentWorkspace?.Id;
        // Skip on initial mount (no previous values to compare)
        if (prevCompany == null && prevWorkspace == null) {
            prevCompanyRef.current = companyId;
            prevWorkspaceRef.current = currWorkspaceId;
            return;
        }
        const companyChanged = prevCompany != null && companyId !== prevCompany;
        const workspaceChanged = prevWorkspace != null && currWorkspaceId !== prevWorkspace;
        // Always update refs to current values so we don't fire twice
        // (e.g. company changes on one render, then workspace follows on the next)
        prevCompanyRef.current = companyId;
        prevWorkspaceRef.current = currWorkspaceId;
        if (companyChanged || workspaceChanged) {
            // Hard reset — don't call completeConversation since the old session
            // belongs to a different company/workspace context
            batch(() => {
                aiChatState.value = {
                    isOpen: false,
                    sessionId: null,
                    messages: [],
                    context: null,
                    isLoading: false,
                    error: null,
                    isMinimized: false,
                    suggestClose: false,
                    rating: null,
                    showDelayedFeedback: false,
                    approachingTurnLimit: false,
                    remainingTurns: 0,
                };
                lastClosedSession.value = null;
                initialSessionContext.value = null;
                fieldUpdateHandler.value = null;
            });
            // After a company change the workspace hasn't updated yet on this render —
            // null out the ref so the follow-up workspace update doesn't trigger a second reset
            if (companyChanged) {
                prevWorkspaceRef.current = undefined;
            }
        }
    }, [companyId, currentWorkspace?.Id]);
    const { provideFeedback, provideMessageFeedback, hideDelayedFeedback, } = useAIChatFeedback(companyId, currentWorkspace);
    const openChat = useCallback(async (contextBuilder) => {
        if (!companyId || !currentWorkspace) {
            showError('Error', 'Workspace not available');
            return;
        }
        const currentState = aiChatState.value;
        if (currentState.isOpen)
            return;
        const context = contextBuilder
            ? contextBuilder()
            : (currentState.context || {
                workspaceId: currentWorkspace.Id,
                location: ChatLocations.Other,
                entityType: ChatEntityTypes.None,
                viewMode: ChatViewModes.View,
                availableFields: [],
                availableActions: ['CopyToClipboard'],
            });
        aiChatState.value = {
            ...currentState,
            isOpen: true,
            isLoading: true,
            context,
            error: null,
            rating: null,
            showDelayedFeedback: false,
            suggestClose: false,
        };
        try {
            const session = await startConversation(companyId, currentWorkspace.Id, context);
            initialSessionContext.value = context;
            // Find first assistant message with its raw backend index
            const firstAssistantIdx = session.Messages.findIndex(m => m.Role === 'assistant');
            const initialMessage = firstAssistantIdx >= 0
                ? {
                    id: generateMessageId(),
                    role: 'assistant',
                    content: session.Messages[firstAssistantIdx].Content,
                    timestamp: new Date(session.Messages[firstAssistantIdx].Timestamp),
                    generatedVariations: session.Messages[firstAssistantIdx].GeneratedVariations,
                    targetFieldName: session.Messages[firstAssistantIdx].TargetFieldName,
                    backendIndex: firstAssistantIdx,
                }
                : {
                    id: generateMessageId(),
                    role: 'assistant',
                    content: 'Hello! How can I help you today?',
                    timestamp: new Date(),
                };
            aiChatState.value = {
                ...aiChatState.value,
                sessionId: session.Id,
                messages: [initialMessage],
                isLoading: false,
                suggestClose: session.SuggestClose || false,
                rating: null,
                ...getTurnLimitFields(session),
            };
        }
        catch (error) {
            console.error('Error opening chat:', error);
            aiChatState.value = {
                ...aiChatState.value,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Failed to start conversation',
            };
            showError('AI Chat Error', 'Failed to start conversation');
        }
    }, [companyId, currentWorkspace, showError]);
    const closeChat = useCallback(() => {
        const currentState = aiChatState.value;
        const currentContext = currentState.context;
        const shouldShowDelayedFeedback = currentState.rating === null || currentState.rating === undefined;
        const closingSessionId = currentState.sessionId;
        batch(() => {
            aiChatState.value = {
                isOpen: false,
                sessionId: null,
                messages: [],
                context: currentContext,
                isLoading: false,
                error: null,
                isMinimized: false,
                suggestClose: false,
                rating: null,
                showDelayedFeedback: shouldShowDelayedFeedback,
                approachingTurnLimit: false,
                remainingTurns: 0,
            };
            if (shouldShowDelayedFeedback && closingSessionId) {
                lastClosedSession.value = closingSessionId;
            }
            initialSessionContext.value = null;
        });
        if (closingSessionId && companyId && currentWorkspace) {
            completeConversation(companyId, currentWorkspace.Id, closingSessionId, 'Dismissed', undefined, undefined, currentState.rating)
                .catch(error => console.error('Error completing conversation:', error));
        }
    }, [companyId, currentWorkspace]);
    const toggleChat = useCallback((contextBuilder) => {
        if (aiChatState.value.isOpen) {
            closeChat();
        }
        else {
            openChat(contextBuilder);
        }
    }, [openChat, closeChat]);
    const sendUserMessage = useCallback(async (message) => {
        const currentState = aiChatState.value;
        if (!currentState.sessionId || !currentState.context || !companyId || !currentWorkspace) {
            showError('Error', 'No active conversation');
            return;
        }
        const userMessage = {
            id: generateMessageId(),
            role: 'user',
            content: message,
            timestamp: new Date(),
        };
        aiChatState.value = {
            ...currentState,
            messages: [...currentState.messages, userMessage],
            isLoading: true,
        };
        try {
            const currentContext = aiChatState.value.context;
            const savedInitialContext = initialSessionContext.value;
            const fieldNamesChanged = (() => {
                const initial = savedInitialContext?.availableFields?.map(f => f.name).sort().join(',') ?? '';
                const current = currentContext?.availableFields?.map(f => f.name).sort().join(',') ?? '';
                return initial !== current;
            })();
            const hasContextChanged = savedInitialContext && currentContext && (savedInitialContext.location !== currentContext.location ||
                savedInitialContext.campaignId !== currentContext.campaignId ||
                savedInitialContext.viewMode !== currentContext.viewMode ||
                fieldNamesChanged);
            const session = await sendMessage(companyId, currentWorkspace.Id, currentState.sessionId, message, hasContextChanged ? currentContext : undefined);
            if (hasContextChanged && currentContext) {
                initialSessionContext.value = currentContext;
            }
            applySessionResponse(session);
        }
        catch (error) {
            console.error('Error sending message:', error);
            const errorMsg = error instanceof Error ? error.message : '';
            const isTurnLimit = errorMsg.toLowerCase().includes('maximum') && errorMsg.toLowerCase().includes('message');
            if (isTurnLimit) {
                aiChatState.value = {
                    ...aiChatState.value,
                    isLoading: false,
                    approachingTurnLimit: true,
                    remainingTurns: 0,
                };
            }
            else {
                aiChatState.value = { ...aiChatState.value, isLoading: false };
                showError('AI Chat Error', 'Failed to send message');
            }
        }
    }, [companyId, currentWorkspace, showError]);
    const applyToField = useCallback(async (fieldName, value, variationIndex, messageIndex) => {
        if (!companyId || !currentWorkspace) {
            showError('Error', 'No workspace selected');
            return;
        }
        try {
            const cleanValue = cleanMarkdown(value);
            const handler = fieldUpdateHandler.value;
            // 1. Apply value to form field immediately (local-first)
            if (!handler) {
                return;
            }
            try {
                await handler(fieldName, cleanValue);
            }
            catch (handlerError) {
                console.error('Field update handler error:', handlerError);
                showError('Error', 'Failed to update field value');
                return;
            }
            // 2. Show success feedback
            const currentState = aiChatState.value;
            const field = currentState.context?.availableFields?.find(f => f.name === fieldName);
            const displayName = field?.displayName || fieldName;
            showSuccess('Applied', `Value applied to ${displayName}`);
            // 3. Record to backend and get AI response
            if (currentState.sessionId) {
                aiChatState.value = { ...aiChatState.value, isLoading: true };
                try {
                    const session = await applyToFieldAPI(companyId, currentWorkspace.Id, currentState.sessionId, fieldName, cleanValue, variationIndex, messageIndex);
                    applySessionResponse(session);
                }
                catch (error) {
                    console.warn('Failed to record AI action:', error);
                    aiChatState.value = { ...aiChatState.value, isLoading: false };
                }
            }
        }
        catch (error) {
            console.error('Error applying value:', error);
            showError('Error', 'Failed to apply value');
        }
    }, [companyId, currentWorkspace, showError, showSuccess]);
    const executeAction = useCallback(async (action) => {
        switch (action.type) {
            case 'ApplyToField':
                if (action.targetField && action.value !== undefined) {
                    await applyToField(action.targetField, action.value, action.variationIndex);
                }
                break;
            case 'CopyToClipboard':
                if (action.value) {
                    await navigator.clipboard.writeText(action.value);
                    showSuccess('Copied', 'Content copied to clipboard');
                }
                break;
            case 'RegenerateVariations':
                await sendUserMessage('Generate more variations');
                break;
            default:
                console.warn('Unknown action type:', action.type);
        }
    }, [applyToField, sendUserMessage, showSuccess]);
    const minimizeChat = useCallback(() => {
        aiChatState.value = { ...aiChatState.value, isMinimized: true };
    }, []);
    const maximizeChat = useCallback(() => {
        aiChatState.value = { ...aiChatState.value, isMinimized: false };
    }, []);
    const setFieldUpdateHandler = useCallback((handler) => {
        fieldUpdateHandler.value = handler;
    }, []);
    return {
        state,
        openChat,
        closeChat,
        toggleChat,
        sendUserMessage,
        applyToField,
        executeAction,
        minimizeChat,
        maximizeChat,
        setFieldUpdateHandler,
        provideFeedback,
        provideMessageFeedback,
        hideDelayedFeedback,
    };
}
/**
 * Hook for only updating AI chat context (doesn't subscribe to state changes)
 * Use this in components that only need to update context, not read it
 */
export function useAIChatContextUpdater() {
    const updateContext = useCallback((newContext) => {
        aiChatState.value = { ...aiChatState.value, context: newContext };
    }, []);
    const setFieldUpdateHandler = useCallback((handler) => {
        fieldUpdateHandler.value = handler;
    }, []);
    return {
        updateContext,
        setFieldUpdateHandler,
    };
}
